
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Ticket, AlertTriangle, Clock, CheckCircle } from 'lucide-react';
import { useTickets } from '@/hooks/useTickets';
import { TicketModal } from '@/components/crm/TicketModal';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { createServiceIntakePort, ServiceIntakePanel } from '@/features/clientService/ServiceIntakePanel';

const serviceIntakePort = createServiceIntakePort(supabase);

export default function CRMTickets() {
  const { user } = useAuth();
  const { tickets, loading } = useTickets();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const getStatusColor = (status: string) => {
    const colors = {
      'open': 'bg-red-100 text-red-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'waiting_customer': 'bg-blue-100 text-blue-800',
      'resolved': 'bg-green-100 text-green-800',
      'closed': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityColor = (priority: string) => {
    const colors = {
      'P1': 'bg-red-100 text-red-800',
      'P2': 'bg-yellow-100 text-yellow-800',
      'P3': 'bg-green-100 text-green-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  const formatStatus = (status: string) => {
    const statuses = {
      'open': 'Open',
      'in_progress': 'In Behandeling',
      'waiting_customer': 'Wacht op Klant',
      'resolved': 'Opgelost',
      'closed': 'Gesloten'
    };
    return statuses[status] || status;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'open':
        return <AlertTriangle className="h-4 w-4" />;
      case 'in_progress':
        return <Clock className="h-4 w-4" />;
      case 'resolved':
      case 'closed':
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <Ticket className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold">Tickets</h1>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-gray-200 rounded-lg h-64 animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  const openTickets = tickets.filter(t => ['open', 'in_progress', 'waiting_customer'].includes(t.status));
  const closedTickets = tickets.filter(t => ['resolved', 'closed'].includes(t.status));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Tickets</h1>
          <p className="text-muted-foreground">
            Beheer support tickets en klantverzoeken
          </p>
        </div>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Nieuw Ticket
        </Button>
      </div>

      <ServiceIntakePanel userId={user?.id ?? null} port={serviceIntakePort} />

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{openTickets.length}</p>
                <p className="text-sm text-muted-foreground">Open</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Clock className="h-5 w-5 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold">
                  {tickets.filter(t => t.status === 'in_progress').length}
                </p>
                <p className="text-sm text-muted-foreground">In Behandeling</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{closedTickets.length}</p>
                <p className="text-sm text-muted-foreground">Opgelost</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Ticket className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{tickets.length}</p>
                <p className="text-sm text-muted-foreground">Totaal</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tickets Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tickets.map((ticket) => (
          <Card
            key={ticket.id}
            className="cursor-pointer hover:shadow-md transition-all duration-200"
            onClick={() => {
              setSelectedTicket(ticket);
              setIsModalOpen(true);
            }}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-2">
                  {getStatusIcon(ticket.status)}
                  <div>
                    <CardTitle className="text-lg font-semibold truncate">
                      {ticket.title}
                    </CardTitle>
                    {ticket.organization && (
                      <p className="text-sm text-muted-foreground">
                        {ticket.organization.name}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  <Badge className={getStatusColor(ticket.status)}>
                    {formatStatus(ticket.status)}
                  </Badge>
                  {ticket.priority && (
                    <Badge variant="outline" className={getPriorityColor(ticket.priority)}>
                      {ticket.priority}
                    </Badge>
                  )}
                </div>
              </div>
            </CardHeader>
            
            <CardContent className="space-y-3">
              {ticket.type && (
                <div className="text-sm text-muted-foreground">
                  Type: {ticket.type}
                </div>
              )}
              
              {ticket.contact && (
                <div className="text-sm text-muted-foreground">
                  Contact: {ticket.contact.first_name} {ticket.contact.last_name}
                </div>
              )}
              
              {ticket.sla_due_at && (
                <div className="text-sm text-muted-foreground">
                  SLA: {format(new Date(ticket.sla_due_at), 'dd MMM yyyy HH:mm', { locale: nl })}
                </div>
              )}
              
              <div className="pt-2 border-t">
                <p className="text-xs text-muted-foreground">
                  Aangemaakt: {format(new Date(ticket.created_at), 'dd MMM yyyy', { locale: nl })}
                </p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {tickets.length === 0 && (
        <Card className="p-8 text-center">
          <Ticket className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Geen tickets</h3>
          <p className="text-gray-500 mb-4">
            Maak je eerste ticket aan om te beginnen.
          </p>
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Nieuw Ticket
          </Button>
        </Card>
      )}

      <TicketModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
      />
    </div>
  );
}
